import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, createCaseMgmtApi, createUsersApi } from '@bloo-team/api-client'
import type {
  CaseMgmtApi, CmAuditEntry, CmCluster, CmEvidence, CmEvidencePage, CmEvidenceUsage, CmNote, CmPage, CmRelatedCase, CmRelation, CmScope, CmSyncResult,
  CmSyncState, CmTimelineEvent, CmTimelineEventKind, CmTimelineKind, CmTimelinePage,
} from '@bloo-team/api-client'
import { client, isDemoSession } from '@bloo-team/core-shell'
import { DEMO_ANALYSTS, demoCaseMgmtApi } from './case-mgmt-demo'
import { currentStatusKey, dispositionLabel } from './case-mgmt'

/**
 * Vantage · Cases · a case's activity (docs/cases.md §Case page): the
 * timeline, the audit log it is joined with, notes, the case's ITSM
 * tickets, evidence, and its relations to other cases. Same demo/live switch and query-key root as lib/case-mgmt.ts, so
 * a case write there refreshes these too.
 */

const liveApi = createCaseMgmtApi(client)
const liveUsersApi = createUsersApi(client)
const api = (): CaseMgmtApi => (isDemoSession() ? demoCaseMgmtApi : liveApi)
const mode = () => (isDemoSession() ? 'demo' : 'live')
const caseKey = (s: CmScope, caseId: string) => ['cm', s.clusterId, s.scopeId, 'activity', mode(), caseId] as const

export const TIMELINE_PAGE = 50
/** The audit log is read whole for the joins, up to this many entries (10 pages of 200). */
const AUDIT_PAGES = 10

/* ---------------------------------------------------------------- timeline */

export function useCmTimeline(s: CmScope, caseId: string, kinds: readonly CmTimelineKind[] | null, enabled = true) {
  return useInfiniteQuery<CmTimelinePage, Error, { pages: CmTimelinePage[] }, readonly unknown[], string | null>({
    queryKey: [...caseKey(s, caseId), 'timeline', kinds?.join(',') ?? 'all'],
    queryFn: ({ pageParam }) => api().timeline(s, caseId, { kinds: kinds ?? undefined, pageSize: TIMELINE_PAGE, cursor: pageParam }),
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    staleTime: 30_000,
    retry: 1,
  })
}

/** The case's audit trail by entry id — the detail a timeline `audit` row does not carry. */
export function useCmAuditIndex(s: CmScope, caseId: string, enabled = true) {
  return useQuery<Map<string, CmAuditEntry>>({
    queryKey: [...caseKey(s, caseId), 'audit'],
    queryFn: async () => {
      const out = new Map<string, CmAuditEntry>()
      for (let page = 1; page <= AUDIT_PAGES; page++) {
        const p = await api().auditLog(s, caseId, { page, pageSize: 200 })
        for (const e of p.items) out.set(e.id, e)
        if (!p.hasMore) break
      }
      return out
    },
    enabled,
    staleTime: 30_000,
    retry: 1,
  })
}

/* ------------------------------------------------------------------- notes */

/** Oldest first, as the service lists them (the Notes thread reads top to bottom). */
export function useCmNotes(s: CmScope, caseId: string, enabled = true) {
  return useQuery<CmNote[]>({
    queryKey: [...caseKey(s, caseId), 'notes'],
    queryFn: () => api().notes(s, caseId),
    enabled,
    staleTime: 30_000,
    retry: 1,
  })
}

/**
 * Refresh everything a case write can change: the case reads and its activity.
 * Not the cases' attached signals (`case-signals`, one workspace read per case
 * on the graphs, each counted as a view — CB-86): none of these writes attach
 * or detach one; an attach refreshes them itself (lib/case-mgmt.ts).
 */
const KEPT_ON_REFRESH = new Set<unknown>(['statuses', 'settings', 'case-signals'])
function useCaseRefresh(s: CmScope) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['cm', s.clusterId, s.scopeId], predicate: (q) => !KEPT_ON_REFRESH.has(q.queryKey[3]) })
}

export function useAddCmNote(s: CmScope, caseId: string) {
  const refresh = useCaseRefresh(s)
  return useMutation<CmNote, Error, string>({ mutationFn: (body) => api().addNote(s, caseId, body), onSuccess: refresh })
}

/* -------------------------------------------------------------------- ITSM */

/**
 * How a case's ticket on one connection is doing: `error` when the last
 * error is newer than the last success (or nothing ever got through),
 * else `warning` while earlier updates sit failed in the outbox, else `ok`.
 * No ticket there (404) or not read yet → null.
 */
export function syncHealth(st: CmSyncState | null | undefined): 'error' | 'warning' | 'ok' | null {
  if (!st) return null
  if (st.lastError && (st.lastSuccessAt === null || (st.lastErrorAt ?? Infinity) > st.lastSuccessAt)) return 'error'
  return st.failed > 0 ? 'warning' : 'ok'
}

/** The case's ticket on each connection; null where it has none (404). */
export function useCmSyncStates(s: CmScope, caseId: string, connectionIds: readonly string[], enabled = true) {
  return useQueries({
    queries: connectionIds.map((id) => ({
      queryKey: [...caseKey(s, caseId), 'itsm', id],
      queryFn: () => api().syncState(s, caseId, id),
      enabled,
      staleTime: 15_000,
      retry: (n: number, e: Error) => n < 1 && !(e instanceof ApiError && e.status === 403),
    })),
  }) as { data: CmSyncState | null | undefined; isLoading: boolean; error: Error | null }[]
}

/**
 * Create or update the case's ticket on one connection; the picked payload
 * presets (`preset_ids`, in pick order — the later wins) are merged into the
 * ticket for this sync.
 */
export function useSyncCmCase(s: CmScope, caseId: string) {
  const refresh = useCaseRefresh(s)
  return useMutation<CmSyncResult, Error, { connectionId: string; presetIds?: string[] }>({
    mutationFn: ({ connectionId, presetIds }) =>
      api().syncToItsm(s, { caseIds: [caseId], configIds: [connectionId], ...(presetIds?.length ? { presetIds: { [connectionId]: presetIds } } : {}) }),
    onSettled: refresh,
  })
}

/* ------------------------------------------------------------------ actors */

/** Login id / email → display name, from the console users list (demo: the demo analysts). */
export function useCmPeople(s: CmScope, enabled = true) {
  return useQuery<Map<string, string>>({
    queryKey: ['users', s.clusterId, s.scopeId, 'people', mode()],
    queryFn: async () => {
      if (isDemoSession()) return new Map([...DEMO_ANALYSTS.map((n) => [n, n] as const), ['demo.analyst', 'Demo Analyst']])
      const out = new Map<string, string>()
      for (const u of await liveUsersApi.list(s)) {
        const name = u.name || u.email_id
        if (!name) continue
        if (u.user_id) out.set(u.user_id, name)
        if (u.email_id) out.set(u.email_id.toLowerCase(), name)
      }
      return out
    },
    enabled,
    staleTime: 300_000,
    retry: 1,
  })
}

const words = (s: string) => s.replace(/[-_.]+/g, ' ').replace(/^\w/, (m) => m.toUpperCase())

/**
 * An actor as people read it: `system:case-grouping` → *Case grouping
 * (system)*, `itsm:<id>` → *<connection name> (ITSM)*, a user's login id →
 * their name from the users list, else the id itself.
 */
export function actorLabel(actor: string | null, people?: Map<string, string>, connections?: Map<string, string>): string {
  if (!actor) return 'Unknown'
  if (actor === 'system') return 'System'
  if (actor.startsWith('system:')) return `${words(actor.slice(7))} (system)`
  if (actor.startsWith('operator:') || actor === 'operator') return 'Platform operator'
  if (actor.startsWith('itsm:')) return `${connections?.get(actor.slice(5)) ?? 'ITSM'} (ITSM)`
  return people?.get(actor) ?? people?.get(actor.toLowerCase()) ?? actor
}

/* ----------------------------------------------------------- notes thread */

/** A note that came in from a linked ticket: an `itsm:<connection>` author (live) or a `… (via <tool>)` label. */
export const isTicketNote = (author: string): boolean => author.startsWith('itsm:') || /\(via [^)]+\)$/.test(author)

/**
 * Who a ticket's note is from and through what: `Customer IT (via Jira)` →
 * *Customer IT* via *Jira*; an `itsm:<connection>` author → the connection's
 * name, via *ticket*. Null for anyone else.
 */
export function ticketSource(author: string, connections?: Map<string, string>): { name: string; via: string } | null {
  if (author.startsWith('itsm:')) return { name: connections?.get(author.slice(5)) ?? 'ITSM', via: 'ticket' }
  const m = /^(.*) \(via ([^)]+)\)$/.exec(author)
  return m ? { name: m[1]!, via: m[2]! } : null
}

/* ------------------------------------------------------------ timeline rows */

/** A rendered timeline row: one event, or a run of identical ones collapsed. */
export interface TimelineRow {
  key: string
  events: CmTimelineEvent[]
}

const runKey = (e: CmTimelineEvent) => `${e.kind}|${e.action ?? ''}|${e.actor ?? ''}|${e.at ?? ''}`

/** Consecutive events with the same kind, action, actor and instant collapse into one row. */
export function collapseRuns(events: readonly CmTimelineEvent[]): TimelineRow[] {
  const rows: TimelineRow[] = []
  for (const e of events) {
    const last = rows[rows.length - 1]
    if (last && runKey(last.events[0]!) === runKey(e)) last.events.push(e)
    else rows.push({ key: e.id, events: [e] })
  }
  return rows
}

/** A timeline day heading: Today, Yesterday, `Wed 14 Oct` (with the year outside this one); Undated without a time. */
export function timelineDayLabel(at: number | null, now = Date.now()): string {
  if (at === null) return 'Undated'
  const d = new Date(at)
  const n = new Date(now)
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((day(n) - day(d)) / 86_400_000)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: d.getFullYear() === n.getFullYear() ? undefined : 'numeric' }).replace(',', '')
}

/** One day of the timeline: its rows, how many events they hold, and how many of each kind (a folded day lists them). */
export interface TimelineDay {
  key: string
  label: string
  rows: TimelineRow[]
  count: number
  kinds: Partial<Record<CmTimelineEventKind, number>>
  /** Today and Yesterday start open; older days start folded. */
  open: boolean
}

/** Rows grouped by local day, in the order given (newest first). */
export function timelineDays(rows: readonly TimelineRow[], now = Date.now()): TimelineDay[] {
  const out: TimelineDay[] = []
  for (const row of rows) {
    const at = row.events[0]!.at
    const label = timelineDayLabel(at, now)
    let day = out[out.length - 1]
    if (day?.label !== label) {
      const d = at === null ? null : new Date(at)
      const key = d ? String(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) : 'undated'
      day = { key, label, rows: [], count: 0, kinds: {}, open: label === 'Today' || label === 'Yesterday' }
      out.push(day)
    }
    day.rows.push(row)
    day.count += row.events.length
    for (const e of row.events) day.kinds[e.kind] = (day.kinds[e.kind] ?? 0) + 1
  }
  return out
}

/** A system, an operator or an ITSM connection — not a person. */
export const isSystemActor = (actor: string | null): boolean => !actor || /^(system|operator|itsm)(:|$)/.test(actor)

/** The timeline's Who list: each actor of the events with its count — people first, then systems, each by count. */
export function actorCounts(events: readonly CmTimelineEvent[]): { actor: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const e of events) counts.set(e.actor ?? '', (counts.get(e.actor ?? '') ?? 0) + 1)
  return [...counts]
    .map(([actor, count]) => ({ actor, count }))
    .sort((a, b) => Number(isSystemActor(a.actor || null)) - Number(isSystemActor(b.actor || null)) || b.count - a.count)
}

/** The fields a `case.updated` entry names, in words; null when it names none. */
export function editedFields(e: CmAuditEntry | undefined): string[] | null {
  const f = e?.detail.updated_fields
  return Array.isArray(f) && f.length ? f.map((x) => String(x).replace(/_/g, ' ')) : null
}

const SEV_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 }

/**
 * What colors a row's node: a signal its severity (a run its highest), a
 * status move its target's category, a note from a linked ticket the second
 * accent, an SLA breach the critical color. Anything else stays neutral (undefined).
 */
export function eventTint(row: TimelineRow, signalSeverity: Map<string, string>, categoryOf: (status: string) => string | undefined): string | undefined {
  const e = row.events[0]!
  if (e.kind === 'signal') {
    const sevs = row.events.map((x) => signalSeverity.get(x.signalId ?? '')).filter((s): s is string => Boolean(s))
    return sevs.sort((a, b) => (SEV_RANK[b] ?? 0) - (SEV_RANK[a] ?? 0))[0]
  }
  if (e.kind === 'transition') return e.toStatus ? categoryOf(e.toStatus) : undefined
  if (e.kind === 'sla' && e.action === 'sla.breached') return 'critical'
  if (e.kind === 'note' && e.actor && isTicketNote(e.actor)) return 'ticket'
  return undefined
}

/** One event as a line to paste: `16 Oct 2026, 14:20 · Arjun Rao · Changed status Open → In progress · case-9141`. */
export function eventCopyLine(at: number | null, who: string, text: string, caseNumber: string): string {
  const when = at === null ? 'Undated' : new Date(at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  return [when, who, text, caseNumber].join(' · ')
}

/** What an audit action says, in words (the service's action names). */
export const AUDIT_LABEL: Record<string, [one: string, many: (n: number) => string]> = {
  'case.created': ['Created the case', (n) => `Created the case (${n})`],
  'case.updated': ['Updated the case', (n) => `Updated the case ${n} times`],
  'entity.attached': ['Attached an entity', (n) => `${n} entities attached`],
  'entity.detached': ['Detached an entity', (n) => `${n} entities detached`],
  'signal.attached': ['Attached a signal', (n) => `${n} signals attached`],
  'note.added': ['Added a note', (n) => `${n} notes added`],
  'case.transitioned': ['Changed status', (n) => `Changed status ${n} times`],
  'case.disposition_changed': ['Changed disposition', (n) => `Changed disposition ${n} times`],
  'itsm.callback': ['Update received from the ITSM', (n) => `${n} updates received from the ITSM`],
  // The audit-backed timeline kinds (itsm, sla, assignment, system).
  'itsm.synced': ['Synced to the ticket', (n) => `Synced to the ticket ${n} times`],
  'itsm.sync_failed': ['Ticket sync failed', (n) => `Ticket sync failed ${n} times`],
  'itsm.verify_requested': ['Verification requested', (n) => `Verification requested ${n} times`],
  'itsm.verify_cancelled': ['Verification cancelled', (n) => `Verification cancelled ${n} times`],
  'sla.breached': ['SLA breached', (n) => `SLA breached ${n} times`],
  'sla.breach_noted': ['SLA breach noted', (n) => `SLA breach noted ${n} times`],
  'sla.retargeted': ['SLA target changed', (n) => `SLA target changed ${n} times`],
  'case.assigned': ['Assigned', (n) => `Assigned ${n} times`],
  'case.unassigned': ['Unassigned', (n) => `Unassigned ${n} times`],
  'case.auto_assigned': ['Auto-assigned', (n) => `Auto-assigned ${n} times`],
  'case.migrated': ['Migrated from the legacy console', (n) => `Migrated (${n})`],
  'case.first_viewed': ['First viewed', (n) => `First viewed (${n})`],
}

/**
 * An assignment as people read it, from its audit entry: *Assigned to <name>*
 * (detail *was <previous>*), *Unassigned* (*was <previous>*); null when the
 * entry names no assignee (the action's label then stands alone).
 */
export function assignmentText(e: CmAuditEntry | undefined): { title: string; detail: string | null } | null {
  const d = e?.detail
  if (!d) return null
  const prev = typeof d.previous_assignee === 'string' && d.previous_assignee ? `was ${d.previous_assignee}` : null
  if (e.action === 'case.unassigned') return { title: 'Unassigned', detail: prev }
  if (typeof d.assignee !== 'string' || !d.assignee) return null
  return { title: `${e.action === 'case.auto_assigned' ? 'Auto-assigned' : 'Assigned'} to ${d.assignee}`, detail: prev }
}

/** A disposition edit (Epic 20) from its audit entry: *Changed disposition A → B*; null when the entry names none. */
export function dispositionChangeText(e: CmAuditEntry | undefined): string | null {
  const d = e?.detail
  if (!d || typeof d.disposition !== 'string') return null
  const prev = typeof d.previous_disposition === 'string' ? d.previous_disposition : null
  return `Changed disposition ${dispositionLabel(prev)} → ${dispositionLabel(d.disposition)}`
}

export const auditLabel = (action: string | null, n = 1) => {
  const known = action ? AUDIT_LABEL[action] : undefined
  if (known) return n > 1 ? known[1](n) : known[0]
  const text = action ? words(action) : 'Changed'
  return n > 1 ? `${text} (${n})` : text
}

/** The audit detail worth a line: changed field names, a status move and its reason, a ticket. */
/**
 * An entity named in an audit entry (`entity.attached` carries `entity_id`)
 * as people read it — `type · name` from the case's attached entities — or
 * null when the entry names none or the entity is not among them.
 */
export function auditEntity(e: CmAuditEntry | undefined, entities: ReadonlyMap<string, { name: string; type: string | null }>): { label: string; id: string } | null {
  const id = e?.detail.entity_id
  if (typeof id !== 'string') return null
  const x = entities.get(id)
  return x ? { label: x.type ? `${x.type.toLowerCase()} · ${x.name}` : x.name, id } : null
}

export function auditDetail(e: CmAuditEntry | undefined): string | null {
  if (!e) return null
  const d = e.detail
  const parts: string[] = []
  if (Array.isArray(d.updated_fields) && d.updated_fields.length) parts.push(d.updated_fields.map(String).join(' · ').replace(/_/g, ' '))
  if (typeof d.from_state === 'string' && typeof d.to_state === 'string') parts.push(`${currentStatusKey(d.from_state)} → ${currentStatusKey(d.to_state)}`)
  if (typeof d.reason === 'string' && d.reason) parts.push(d.reason)
  if (typeof d.origin === 'string' && e.action === 'case.created') parts.push(d.origin)
  if (typeof d.entity_id === 'string') parts.push(d.entity_id)
  if (typeof d.signal_id === 'string') parts.push(d.signal_id)
  if (typeof d.customer_ticket_id === 'string') parts.push(d.customer_ticket_id)
  if (typeof d.itsm_status === 'string') parts.push(d.itsm_status)
  return parts.length ? parts.join(' · ') : null
}

/* ---------------------------------------------------------------- evidence */

export type EvidenceSort = 'uploaded_at_desc' | 'size_desc'

/**
 * A case's evidence files (the service pages them; a case holds at most a
 * few dozen, so the tab reads up to 200). A 404 here, for a case that exists,
 * means the operator has evidence attachments off for the tenant.
 */
export function useCaseEvidence(s: CmScope, caseId: string, sort: EvidenceSort, includeDeleted: boolean, enabled = true) {
  return useQuery<CmEvidencePage>({
    queryKey: [...caseKey(s, caseId), 'evidence', sort, includeDeleted],
    queryFn: () => api().evidence(s, caseId, { page: 1, pageSize: 200, sort, includeDeleted }),
    enabled,
    staleTime: 30_000,
    retry: (n, e) => n < 1 && !(e instanceof ApiError && [403, 404].includes(e.status)),
  })
}

/** The tenant's evidence limits and use: per-file max, files per case, quota. */
export function useEvidenceUsage(s: CmScope, enabled = true) {
  return useQuery<CmEvidenceUsage>({
    queryKey: ['cm', s.clusterId, s.scopeId, 'evidence-usage', mode()],
    queryFn: () => api().evidenceUsage(s),
    enabled,
    staleTime: 30_000,
    retry: (n, e) => n < 1 && !(e instanceof ApiError && [403, 404].includes(e.status)),
  })
}

export interface UploadOutcome {
  file: string
  ok: boolean
  /** The service's reason when refused. */
  error: string | null
}

/** Upload several files one after another; each gets its own outcome, a refusal does not stop the rest. */
export function useUploadEvidence(s: CmScope, caseId: string) {
  const refresh = useCaseRefresh(s)
  const qc = useQueryClient()
  return useMutation<UploadOutcome[], Error, { files: File[]; description?: string }>({
    mutationFn: async ({ files, description }) => {
      const out: UploadOutcome[] = []
      for (const file of files) {
        try {
          await api().uploadEvidence(s, caseId, file, description)
          out.push({ file: file.name, ok: true, error: null })
        } catch (e) {
          out.push({ file: file.name, ok: false, error: e instanceof Error ? e.message : 'upload failed' })
        }
      }
      return out
    },
    onSettled: () => {
      void refresh()
      void qc.invalidateQueries({ queryKey: ['cm', s.clusterId, s.scopeId, 'evidence-usage'] })
    },
  })
}

export function useEditEvidenceDescription(s: CmScope, caseId: string) {
  const refresh = useCaseRefresh(s)
  return useMutation<CmEvidence, Error, { id: string; description: string | null }>({
    mutationFn: ({ id, description }) => api().updateEvidence(s, caseId, id, description),
    onSuccess: refresh,
  })
}

/** The console's only delete (evidence, Security Engineer, closed case). */
export function useDeleteEvidence(s: CmScope, caseId: string) {
  const refresh = useCaseRefresh(s)
  const qc = useQueryClient()
  return useMutation<CmEvidence, Error, string>({
    mutationFn: (id) => api().deleteEvidence(s, caseId, id),
    onSuccess: () => {
      void refresh()
      void qc.invalidateQueries({ queryKey: ['cm', s.clusterId, s.scopeId, 'evidence-usage'] })
    },
  })
}

/** The file's bytes — one audited download (Download and View both use it). */
export const fetchEvidence = (s: CmScope, caseId: string, evidenceId: string) => api().downloadEvidence(s, caseId, evidenceId)

/** Save the bytes under the file's stored name. */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/* --------------------------------------------------------------- relations */

/** A case's relations, a page at a time; dismissed ones only when asked. */
export function useCmRelations(s: CmScope, caseId: string, q: { page: number; pageSize: number; includeDismissed: boolean }) {
  return useQuery<CmPage<CmRelation>>({
    queryKey: [...caseKey(s, caseId), 'relations', q],
    queryFn: () => api().relations(s, caseId, q),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    retry: 1,
  })
}

/**
 * This case's relations (dismissed too) by the other case's UUID — a
 * timeline relation event names the other case by UUID only; this gives its
 * number, title and the shared entities. One read of up to 200 relations
 * (the service's page cap).
 */
export function useCmRelatedCases(s: CmScope, caseId: string, enabled = true) {
  return useQuery<ReadonlyMap<string, CmRelation>>({
    queryKey: [...caseKey(s, caseId), 'relations', 'by-case'],
    queryFn: async () => {
      const page = await api().relations(s, caseId, { page: 1, pageSize: 200, includeDismissed: true })
      return new Map(page.items.map((r) => [r.other.id, r]))
    },
    enabled,
    staleTime: 30_000,
    retry: 1,
  })
}

/** Every relation of this case, page after page of 200 (the service's page cap) — the Relations graph's cases. */
export function useAllCmRelations(s: CmScope, caseId: string, includeDismissed: boolean, enabled = true) {
  return useQuery<CmRelation[]>({
    queryKey: [...caseKey(s, caseId), 'relations', 'all', includeDismissed],
    queryFn: async () => {
      const out: CmRelation[] = []
      for (let page = 1; ; page++) {
        const p = await api().relations(s, caseId, { page, pageSize: 200, includeDismissed })
        out.push(...p.items)
        if (!p.items.length || out.length >= p.total) return out
      }
    },
    enabled,
    staleTime: 30_000,
    retry: 1,
  })
}

export const CANDIDATES_PAGE = 10

/** Cases that can be linked to this one: the service's title / id search, title matches first, then most recently updated. */
export function useCmRelationCandidates(s: CmScope, caseId: string, q: string, page: number, enabled = true) {
  return useQuery<CmPage<CmRelatedCase>>({
    queryKey: [...caseKey(s, caseId), 'candidates', q.trim(), page],
    queryFn: () => api().relationCandidates(s, caseId, { q, page, pageSize: CANDIDATES_PAGE }),
    enabled,
    placeholderData: (prev) => prev,
    staleTime: 15_000,
    retry: 1,
  })
}

/** Link one case (the service takes one per call); the counts, relations, cluster and timeline refresh. */
/** Link another case — its UUID or its `case-N`. */
export function useLinkCmCase(s: CmScope, caseId: string) {
  const refresh = useCaseRefresh(s)
  return useMutation<CmRelation, Error, string>({ mutationFn: (other) => api().linkCase(s, caseId, other), onSuccess: refresh })
}

/** Remove a link: the service deletes a manual one and dismisses an automatic one. */
export function useUnlinkCmCase(s: CmScope, caseId: string) {
  const refresh = useCaseRefresh(s)
  return useMutation<void, Error, string>({ mutationFn: (relationId) => api().unlinkCase(s, caseId, relationId), onSuccess: refresh })
}

/** The cases reachable from this one through active relations, within `hops`. */
export function useCmCluster(s: CmScope, caseId: string, hops: number) {
  return useQuery<CmCluster>({
    queryKey: [...caseKey(s, caseId), 'cluster', hops],
    queryFn: () => api().cluster(s, caseId, { hops }),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    retry: 1,
  })
}
